import { useContext, useEffect, useMemo, useState } from 'react';

import { mockFn } from 'src/tools/fn.tools';
import { useFuzzyFinder } from 'src/hooks/useFuzzyFinder';

import { ButtonBaseType, ID, Maybe } from 'src/models';
import { InputContext } from 'src/contexts/input.context';
import { ParserContext } from 'src/contexts/parser.context';

import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { BaseComponentProps } from 'src/components/Dropdown';
import { t } from 'src/tools/text.tools';
import { ifTrue } from 'src/tools/logic.tools';
import { SEARCH_KEYS } from 'src/data/misc.data';

const TXT = t(['dropdown']);

export interface SearchableDropdownInputProps extends BaseComponentProps {
  value?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onDiscard: () => void;
}

export interface SearchableDropdownProps {
  items?: ButtonBaseType[];
  isSearchable?: boolean;
  renderInput?: (props: SearchableDropdownInputProps) => JSX.Element;
  renderItemLabel?: (item: ButtonBaseType, isSelected: boolean, isActive: boolean) => string | JSX.Element;
  isStrictSearch?: boolean;
}

export function SearchableDropdown({
  renderItemLabel,
  items: propsItems,
  isSearchable,
  renderInput,
  isStrictSearch,
}: SearchableDropdownProps) {
  const {
    value,
    onChange,
    classNames,
    onDiscard,
    isDisabled,
    isReadonly,
    isAutoFocus,
    onClick,
    customization = {},
    placeholder,
    isLoading,
    items: contextItems,
  } = useContext(InputContext);

  const parserContext = useContext(ParserContext);

  const items = useMemo(() => propsItems ?? contextItems ?? [], [contextItems, propsItems]);

  const readOnly = useMemo(() => isReadonly ?? !parserContext.isActive, [isReadonly, parserContext.isActive]);

  const disabled = useMemo(() => isDisabled ?? parserContext.isDisabled, [isDisabled, parserContext.isDisabled]);

  const { dropdownMinWidth = 'container-width', isSmallMenu, isInline, dropdownPosition, isHideSplits } = customization;

  const itemId = useMemo(() => value ?? parserContext.value, [value, parserContext.value]);

  const currentItem = useMemo(() => itemId && items.find(i => i.id === itemId), [itemId, items]);

  const [search, setSearch] = useState<Maybe<string>>();

  const [isOpen, setIsOpen] = useState(false);

  const { makeArrayDataToRender } = useFuzzyFinder(SEARCH_KEYS, isStrictSearch);

  const isItemsNotAvailable = !disabled && !readOnly && !items.length;

  useEffect(() => {
    if (!readOnly) setIsOpen(true);
  }, [readOnly]);

  function handleOnItemClick(id: ID) {
    onChange ? onChange(id, true) : parserContext.onChange?.(id, true);
    setSearch(undefined);
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();

      if (parserContext.onClick) parserContext.onClick();
    };
  }

  function handleOnChange(val: string) {
    setSearch(val);
    setIsOpen(true);
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) {
      onDiscard ? onDiscard() : parserContext.onDiscard?.();
      setSearch(undefined);
    }
  }

  function handleOnDiscard() {
    handleOnRequestOpenClose(false);
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const extendedProps = {
      value: search ?? currentItem?.label ?? itemId,
      onDiscard: handleOnDiscard,
      onSubmit: handleOnDiscard,
      onChange: handleOnChange,
      onClick: getHandleOnClick(baseComponentProps.onClick),
      onFocus: isSearchable ? baseComponentProps.onFocus : mockFn,
    };

    if (renderInput)
      return renderInput({
        ...baseComponentProps,
        ...extendedProps,
      });

    return (
      <InputWithIcons
        {...extendedProps}
        className={classNames?.input ?? parserContext.classNames?.input}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled || !items.length}
        autoFocus={isAutoFocus}
        rightIconId={baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down'}
        readOnly={readOnly || !isSearchable}
        placeholder={placeholder}
        isSmall={customization.isSmall}
        isXSmall={customization.isXSmall}
      />
    );
  }

  return (
    <DropdownMenu
      tooltip={ifTrue(isItemsNotAvailable, TXT('dropdownValuesIsNotAvailable'))}
      classNames={classNames ?? parserContext.classNames}
      items={makeArrayDataToRender(search, items)}
      currentItemId={itemId}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      renderItemLabel={renderItemLabel}
      dropdownMinWidth={dropdownMinWidth}
      isOpen={isOpen}
      onRequestOpenClose={handleOnRequestOpenClose}
      isSmall={isSmallMenu}
      isInline={isInline}
      position={dropdownPosition}
      isHideSplits={isHideSplits}
      isLoading={isLoading}
    />
  );
}
